import { cva, type VariantProps } from 'class-variance-authority'
import type { ButtonHTMLAttributes } from 'react'
import { Slot } from '@radix-ui/react-slot'
import { forwardRef } from 'react'

import { cn } from 'utils/classMerge'

import Text from '../text'

const buttonVariants = cva(
  'tw-m-0 tw-appearance-none tw-bg-none tw-p-0 tw-transition-all tw-duration-300 disabled:tw-cursor-not-allowed',
  {
    variants: {
      variant: {
        'primary-solid':
          'tw-rounded-md tw-border tw-border-solid tw-border-orange-500 tw-bg-transparent tw-font-medium tw-leading-none tw-text-orange-500 hover:tw-border-orange-600 hover:tw-text-orange-600 active:tw-border-orange-600 active:tw-text-orange-600 disabled:tw-border-zinc-300 disabled:tw-text-zinc-400',
        primary:
          'tw-w-fit tw-rounded tw-border-none tw-bg-blue-500 tw-px-10 tw-py-4 tw-text-sm tw-font-medium tw-uppercase tw-tracking-[1.12px] tw-text-white',
        link: 'tw-border-none tw-bg-white tw-uppercase tw-text-blue-500 tw-underline',
        box: 'tw-font-primary tw-flex tw-flex-col tw-items-center tw-justify-center tw-whitespace-pre-wrap tw-border tw-border-solid tw-border-zinc-200 tw-bg-white tw-p-4 hover:tw-border-orange md:tw-flex-row',
        reload:
          'tw-flex tw-w-full tw-items-center tw-justify-center tw-rounded-sm tw-border tw-border-solid tw-border-orange tw-bg-white tw-px-4 tw-py-2 tw-uppercase',
        legacy:
          'tw-m-0 tw-flex tw-h-12 tw-w-full tw-items-center tw-justify-center tw-whitespace-nowrap tw-rounded-sm tw-border tw-border-solid tw-border-white tw-bg-orange tw-py-0 tw-font-roboto tw-text-sm tw-font-bold tw-uppercase tw-tracking-[0.1px] tw-text-white',
        legacySecondary:
          'tw-m-0 tw-flex tw-h-12 tw-w-full tw-items-center tw-justify-center tw-whitespace-nowrap tw-rounded-sm tw-border tw-border-solid tw-border-zinc-400 tw-bg-white tw-py-0 tw-font-roboto tw-text-sm tw-font-bold tw-uppercase tw-tracking-[0.1px] tw-text-zinc-400',
        neutralOutline:
          'tw-flex tw-items-center tw-justify-center tw-rounded-md tw-border tw-border-solid tw-border-zinc-300 tw-bg-transparent tw-text-zinc-600 hover:tw-border-zinc-400 hover:tw-text-zinc-900 active:tw-border-zinc-400 active:tw-text-zinc-900 disabled:tw-border-zinc-300 disabled:tw-text-zinc-400',
        outlined:
          'tw-font-primary tw-flex tw-flex-col tw-items-center tw-justify-center tw-whitespace-pre-wrap tw-rounded-md tw-border tw-border-solid tw-border-zinc-200 tw-bg-white tw-px-4 tw-text-base tw-font-medium hover:tw-border-orange md:tw-flex-row',
        onlyText:
          'tw-border-none tw-bg-white tw-text-base tw-text-sm tw-capitalize tw-text-zinc-600 tw-no-underline',
      },
      size: {
        xs: 'tw-h-6 tw-px-2 tw-py-1 tw-text-xs',
        sm: 'tw-h-7 tw-px-2 tw-py-1 tw-text-sm',
        default: 'tw-h-9 tw-px-3 tw-py-2 tw-text-sm',
        lg: 'tw-h-10 tw-px-4 tw-py-2 tw-text-base',
      },
    },
    defaultVariants: {
      variant: 'primary',
    },
  },
)
interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  testId?: string
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { testId, className, variant, size, asChild = false, children, ...props },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button'

    const buttonComponent = (
      <Comp
        data-testid={testId}
        type="button"
        className={cn(buttonVariants({ variant, size }), className)}
        ref={ref}
        {...props}
      >
        <Text
          renderChildren
          as="div"
          className="tw-flex tw-items-center tw-justify-center tw-gap-2"
        >
          {children}
        </Text>
      </Comp>
    )

    return typeof children === 'string' ? (
      <Text asChild skipTranslation>
        {buttonComponent}
      </Text>
    ) : (
      buttonComponent
    )
  },
)

Button.displayName = 'Button'

export { Button }
